import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { bulkRolesApi, pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Users, Play, Square, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function BulkRolesPlugin({ serverId }: { serverId: string }) {
  const parseUserIds = (input: string): string[] => {
    const matches = String(input || '').match(/\d{16,20}/g) || [];
    return Array.from(new Set(matches));
  };

  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
  });

  const bulkRolesPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'bulk-roles');
  const isEnabled = bulkRolesPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'bulk-roles') : pluginApi.disablePlugin(serverId, 'bulk-roles'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'bulk-roles', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const { data: runnerStatus, refetch: refetchRunnerStatus, isFetching: statusLoading } = useQuery({
    queryKey: ['servers', serverId, 'bulk-roles', 'status'],
    queryFn: () => bulkRolesApi.getStatus(serverId),
    enabled: isEnabled,
    refetchInterval: 5000,
  });

  const { data: runnerLogs, refetch: refetchRunnerLogs, isFetching: logsLoading } = useQuery({
    queryKey: ['servers', serverId, 'bulk-roles', 'logs'],
    queryFn: () => bulkRolesApi.getLogs(serverId, 100),
    enabled: isEnabled,
    refetchInterval: 5000,
  });

  const { mutate: startRunner, isPending: startingRunner } = useApiMutation(
    (payload: {
      roleFromId: string;
      roleToId: string;
      intervalSeconds: number;
      maxUsersPerCycle?: number;
      includeUserIds?: string[];
      excludeUserIds?: string[];
    }) =>
      bulkRolesApi.start(serverId, payload),
    {
      onSuccess: () => {
        toast.success('Auto runner started');
        refetchRunnerStatus();
        refetchRunnerLogs();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to start auto runner');
      },
    }
  );

  const { mutate: stopRunner, isPending: stoppingRunner } = useApiMutation(
    () => bulkRolesApi.stop(serverId),
    {
      onSuccess: () => {
        toast.success('Auto runner stopped');
        refetchRunnerStatus();
        refetchRunnerLogs();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to stop auto runner');
      },
    }
  );

  const { mutate: runRunnerOnce, isPending: runningOnce } = useApiMutation(
    () => bulkRolesApi.runOnce(serverId),
    {
      onSuccess: () => {
        toast.success('Esecuzione manuale completata');
        refetchRunnerStatus();
        refetchRunnerLogs();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to run manual cycle');
      },
    }
  );

  const { mutate: assignRoleToAll, isPending: assigningAll } = useApiMutation(
    (payload: { roleId: string; batchSize?: number; includeBots?: boolean }) =>
      bulkRolesApi.assignAll(serverId, payload),
    {
      onSuccess: (response: any) => {
        const result = response?.result;
        toast.success(
          `Ruolo assegnato. Successi: ${result?.successCount ?? 0}, falliti: ${result?.failCount ?? 0}`
        );
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore durante assegnazione ruolo a tutti');
      },
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    requireConfirmation: true,
    logActions: true,
    logChannelId: '',
    maxBatchSize: 1000,
    autoRunner: {
      enabled: false,
      roleFromId: '',
      roleToId: '',
      intervalSeconds: 60,
      maxUsersPerCycle: 0,
      includeUserIds: [],
      excludeUserIds: [],
    }
  });
  const [includeUserIdsInput, setIncludeUserIdsInput] = useState('');
  const [excludeUserIdsInput, setExcludeUserIdsInput] = useState('');
  const [assignAllRoleId, setAssignAllRoleId] = useState('');
  const [assignAllIncludeBots, setAssignAllIncludeBots] = useState(false);

  useEffect(() => {
    if (bulkRolesPlugin?.config) {
      setConfig((prev: any) => ({
        ...prev,
        ...bulkRolesPlugin.config,
        autoRunner: {
          ...prev.autoRunner,
          ...(bulkRolesPlugin.config?.autoRunner || {}),
        },
      }));

      const includeUserIds = Array.isArray(bulkRolesPlugin.config?.autoRunner?.includeUserIds)
        ? bulkRolesPlugin.config.autoRunner.includeUserIds
        : [];
      const excludeUserIds = Array.isArray(bulkRolesPlugin.config?.autoRunner?.excludeUserIds)
        ? bulkRolesPlugin.config.autoRunner.excludeUserIds
        : [];

      setIncludeUserIdsInput(includeUserIds.join('\n'));
      setExcludeUserIdsInput(excludeUserIds.join('\n'));
    }
  }, [bulkRolesPlugin]);

  const handleSave = () => {
    const includeUserIds = parseUserIds(includeUserIdsInput);
    const excludeUserIds = parseUserIds(excludeUserIdsInput);

    updateConfig({
      ...config,
      autoRunner: {
        ...config.autoRunner,
        includeUserIds,
        excludeUserIds,
      },
    });
  };

  const handleStartRunner = () => {
    const roleFromId = String(config?.autoRunner?.roleFromId || '').trim();
    const roleToId = String(config?.autoRunner?.roleToId || '').trim();
    const intervalSeconds = Math.max(10, Number(config?.autoRunner?.intervalSeconds || 60));
    const maxUsersPerCycle = Math.max(0, Number(config?.autoRunner?.maxUsersPerCycle || 0));
    const includeUserIds = parseUserIds(includeUserIdsInput);
    const excludeUserIds = parseUserIds(excludeUserIdsInput);

    if (!roleFromId || !roleToId) {
      toast.error('Inserisci roleFromId e roleToId');
      return;
    }

    if (includeUserIds.length > 0 && excludeUserIds.length > 0) {
      const overlap = includeUserIds.some((id) => excludeUserIds.includes(id));
      if (overlap) {
        toast.error('Gli stessi utenti non possono essere sia inclusi che esclusi');
        return;
      }
    }

    startRunner({
      roleFromId,
      roleToId,
      intervalSeconds,
      maxUsersPerCycle,
      includeUserIds,
      excludeUserIds,
    });
  };

  const handleStopRunner = () => {
    stopRunner();
  };

  const handleRunNow = () => {
    runRunnerOnce();
  };

  const handleAssignAll = () => {
    const roleId = String(assignAllRoleId || '').trim();
    if (!roleId) {
      toast.error('Seleziona un ruolo da assegnare');
      return;
    }

    assignRoleToAll({
      roleId,
      batchSize: Math.max(1, Math.min(1000, Number(config.maxBatchSize || 25))),
      includeBots: assignAllIncludeBots,
    });
  };

  const handleAssignAllFromRunnerRoleTo = () => {
    const roleId = String(config?.autoRunner?.roleToId || '').trim();
    if (!roleId) {
      toast.error('Seleziona prima il Role To ID');
      return;
    }

    assignRoleToAll({
      roleId,
      batchSize: Math.max(1, Math.min(1000, Number(config.maxBatchSize || 25))),
      includeBots: assignAllIncludeBots,
    });
  };

  if (!bulkRolesPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-500/20 rounded-xl">
            <Users className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Bulk Role Management</h3>
            <p className="text-sm text-muted-foreground">
              Manage roles for multiple users at once.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div>
              <Label>Max Batch Size</Label>
              <Input
                type="number"
                min="10"
                max="10000"
                value={config.maxBatchSize}
                onChange={(e) => setConfig({ ...config, maxBatchSize: parseInt(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum users to process in one operation
              </p>
            </div>

            <div>
              <Label>Log Channel (Optional)</Label>
              <Select
                value={config.logChannelId || 'none'}
                onValueChange={(value) => setConfig({ ...config, logChannelId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona canale log" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun canale</SelectItem>
                  {(channels as any[]).map((channel: any) => (
                    <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <Label className="text-sm">Require Confirmation</Label>
                <Switch
                  checked={config.requireConfirmation}
                  onCheckedChange={(checked) => setConfig({ ...config, requireConfirmation: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <Label className="text-sm">Log All Actions</Label>
                <Switch
                  checked={config.logActions}
                  onCheckedChange={(checked) => setConfig({ ...config, logActions: checked })}
                />
              </div>
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold">Available Commands</h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-indigo-400">/assignrole role:@ruolo filter:all</code>
                <p className="text-muted-foreground mt-1">Add role to all members without that role</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-indigo-400">/removerole role:@ruolo filter:all</code>
                <p className="text-muted-foreground mt-1">Remove role from all members with that role</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-indigo-400">/migrateroles role_from:@A role_to:@B</code>
                <p className="text-muted-foreground mt-1">Replace a role for all members who have source role</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold">Aggiungi ruolo a tutti gli utenti</h4>
            <p className="text-sm text-muted-foreground">
              Assegna un ruolo a tutti i membri che ancora non lo possiedono.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Ruolo da assegnare</Label>
                <Select
                  value={assignAllRoleId || 'none'}
                  onValueChange={(value) => setAssignAllRoleId(value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun ruolo</SelectItem>
                    {(roles as any[]).map((role: any) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg w-full">
                  <Label className="text-sm">Includi bot</Label>
                  <Switch
                    checked={assignAllIncludeBots}
                    onCheckedChange={(checked) => setAssignAllIncludeBots(checked)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleAssignAll} disabled={assigningAll} className="gap-2">
                {assigningAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                Aggiungi ruolo a tutti
              </Button>
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Auto Replace Runner</h4>
              <div className="text-xs text-muted-foreground">
                Stato: {runnerStatus?.status?.running ? 'Attivo' : 'Fermo'}
                {runnerStatus?.status?.inProgress ? ' • In esecuzione' : ''}
              </div>
            </div>

            <div className="text-xs text-muted-foreground bg-white/5 border border-white/10 rounded-lg p-3">
              Per assegnare un ruolo a tutti senza usare Role From ID: seleziona solo <span className="font-semibold text-foreground">Role To ID</span> e clicca <span className="font-semibold text-foreground">Assegna Role To a tutti</span>.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Role From ID</Label>
                <Select
                  value={config?.autoRunner?.roleFromId || 'none'}
                  onValueChange={(value) =>
                    setConfig({
                      ...config,
                      autoRunner: { ...config.autoRunner, roleFromId: value === 'none' ? '' : value },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona ruolo da rimuovere" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun ruolo</SelectItem>
                    {(roles as any[]).map((role: any) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Role To ID</Label>
                <Select
                  value={config?.autoRunner?.roleToId || 'none'}
                  onValueChange={(value) =>
                    setConfig({
                      ...config,
                      autoRunner: { ...config.autoRunner, roleToId: value === 'none' ? '' : value },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona ruolo da aggiungere" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun ruolo</SelectItem>
                    {(roles as any[]).map((role: any) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Intervallo (secondi)</Label>
                <Input
                  type="number"
                  min="10"
                  value={config?.autoRunner?.intervalSeconds || 60}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      autoRunner: {
                        ...config.autoRunner,
                        intervalSeconds: Math.max(10, Number(e.target.value) || 60),
                      },
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Max utenti per ciclo (0 = tutti)</Label>
                <Input
                  type="number"
                  min="0"
                  value={config?.autoRunner?.maxUsersPerCycle || 0}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      autoRunner: {
                        ...config.autoRunner,
                        maxUsersPerCycle: Math.max(0, Number(e.target.value) || 0),
                      },
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Utenti inclusi (opzionale)</Label>
                <textarea
                  value={includeUserIdsInput}
                  onChange={(e) => setIncludeUserIdsInput(e.target.value)}
                  className="min-h-24 w-full rounded-md border border-white/15 bg-black/20 px-3 py-2 text-sm"
                  placeholder="Un ID utente per riga"
                />
                <p className="text-xs text-muted-foreground mt-1">Se valorizzato, il runner lavora solo su questi utenti.</p>
              </div>
              <div>
                <Label>Utenti esclusi (opzionale)</Label>
                <textarea
                  value={excludeUserIdsInput}
                  onChange={(e) => setExcludeUserIdsInput(e.target.value)}
                  className="min-h-24 w-full rounded-md border border-white/15 bg-black/20 px-3 py-2 text-sm"
                  placeholder="Un ID utente per riga"
                />
                <p className="text-xs text-muted-foreground mt-1">Questi utenti vengono sempre saltati.</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleStartRunner} disabled={startingRunner || stoppingRunner} className="gap-2">
                {startingRunner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Avvia funzione
              </Button>
              <Button variant="destructive" onClick={handleStopRunner} disabled={startingRunner || stoppingRunner} className="gap-2">
                {stoppingRunner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                Stop funzione
              </Button>
              <Button
                variant="secondary"
                onClick={handleRunNow}
                disabled={runningOnce || startingRunner || stoppingRunner || !!runnerStatus?.status?.inProgress}
                className="gap-2"
              >
                {runningOnce ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Esegui adesso
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  refetchRunnerStatus();
                  refetchRunnerLogs();
                }}
                disabled={statusLoading || logsLoading}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${(statusLoading || logsLoading) ? 'animate-spin' : ''}`} />
                Aggiorna log
              </Button>
              <Button
                variant="default"
                onClick={handleAssignAllFromRunnerRoleTo}
                disabled={assigningAll || startingRunner || stoppingRunner || runningOnce}
                className="gap-2"
              >
                {assigningAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                Assegna Role To a tutti
              </Button>
            </div>

            <div className="text-xs text-muted-foreground">
              <div>Ultimo run: {runnerStatus?.status?.lastRunAt ? new Date(runnerStatus.status.lastRunAt).toLocaleString() : 'N/A'}</div>
              <div>Prossimo run: {runnerStatus?.status?.nextRunAt ? new Date(runnerStatus.status.nextRunAt).toLocaleString() : 'N/A'}</div>
              <div>Esito ultimo run: {runnerStatus?.status?.lastSummary || 'N/A'}</div>
            </div>

            <div className="bg-black/20 border border-white/10 rounded-lg p-3 max-h-72 overflow-auto">
              <div className="text-xs font-semibold mb-2">Log attività</div>
              <div className="space-y-1">
                {(runnerLogs?.logs || []).length === 0 && (
                  <div className="text-xs text-muted-foreground">Nessun log disponibile.</div>
                )}
                {(runnerLogs?.logs || []).map((row, idx) => (
                  <div key={`${row.timestamp}-${idx}`} className="text-xs text-muted-foreground break-words flex items-start gap-2">
                    <span className="shrink-0">[{new Date(row.timestamp).toLocaleTimeString()}]</span>
                    <span
                      className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        row.level === 'error'
                          ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                          : row.level === 'warn'
                            ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      }`}
                    >
                      {row.level.toUpperCase()}
                    </span>
                    <span className="min-w-0">{row.message}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
